import {
  type DeployLlmKeyRow,
  fetchEnvironmentById,
  fetchLlmGatewayConfiguration,
  fetchLlmKeys,
  fetchLlmKeysByEnvId,
  fetchLlmPolicy,
  revokeLlmKey,
  selectLlmGatewayConfiguration,
  selectLlmKeysByEnvIdForTable,
  selectLlmKeysForTable,
} from "@app/deploy";
import { compactNumber } from "@app/number-utils";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useCompositeLoader,
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { aiGatewayEnrollUrl, llmKeyDetailUrl, supportUrl } from "@app/routes";
import { Banner } from "@app/ui/shared/banner";
import { Button, ButtonLink } from "@app/ui/shared/button";
import { DataTable, idColDef } from "@app/ui/shared/datatable";
import {
  CurrencyCell,
  DataTableCell,
  DateTimeCell,
  EnvironmentCell,
  PillCell,
} from "@app/ui/shared/datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "@app/ui/shared/datatable/datatable";
import { FormGroup } from "@app/ui/shared/form-group";
import { Group } from "@app/ui/shared/group";
import { Input } from "@app/ui/shared/input";
import { LoadingSpinner } from "@app/ui/shared/loading";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { TitleBar } from "@app/ui/shared/resource-list-view";
import { tokens } from "@app/ui/shared/tokens";
import { Tooltip } from "@app/ui/shared/tooltip";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CollapsibleBanner } from "../collapsible-banner";
import { ExternalLink } from "../external-link";
import { LlmPolicyForEnvironment } from "../llm-policy";
import { OrgAiUsageSummary } from "./llm-organization-usage";

// --- Revoke confirmation modal ---
// Rendered at the page level (outside DataTable) to avoid ag-grid's CSS
// transform stacking context swallowing the fixed-position overlay.

const RevokeKeyModal = ({
  isOpen,
  onClose,
  llmKey,
}: {
  isOpen: boolean;
  onClose: () => void;
  llmKey: DeployLlmKeyRow | null;
}) => {
  const dispatch = useDispatch();
  const loader = useLoader(revokeLlmKey);
  const orgId = useSelector(selectOrganizationSelectedId);
  const [confirm, setConfirm] = useState("");

  useLoaderSuccess(loader, onClose);

  useEffect(() => {
    if (!isOpen) return;
    setConfirm("");
  }, [isOpen]);

  if (!isOpen || !llmKey) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose} className="min-w-[500px]">
        <ModalHeading title="Revoke LLM Key" onClose={onClose} />
        <ModalBody>
          <p className="text-gray-700 mb-4">
            This will permanently revoke <strong>{llmKey.handle}</strong>. Any
            applications using this key will immediately lose access to the AI
            Gateway. This action cannot be undone.
          </p>
          <FormGroup
            label={`Type "${llmKey.handle}" to confirm`}
            htmlFor="revoke-confirm"
          >
            <Input
              id="revoke-confirm"
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={llmKey.handle}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter
          actions={[
            <Button key="cancel" variant="white" onClick={onClose}>
              Cancel
            </Button>,
            <Button
              key="revoke"
              variant="delete"
              disabled={confirm !== llmKey.handle}
              isLoading={loader.isLoading}
              onClick={() => dispatch(revokeLlmKey({ id: llmKey.id, orgId }))}
            >
              Revoke Key
            </Button>,
          ]}
        />
      </Modal>
    </ModalWrapper>
  );
};

// --- Column definitions ---

const colDefs: Record<string, DataTableColDef<DeployLlmKeyRow>> = {
  handle: {
    field: "handle",
    headerName: "Key",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <DataTableCell
        label={data.handle}
        href={llmKeyDetailUrl(data.id)}
        description={data.note ?? undefined}
        icon={
          <img
            src="/resource-types/logo-llm_key.png"
            className="w-[32px] h-[32px] mr-2 align-middle"
            aria-label="LLM Key"
          />
        }
      />
    ),
  },
  id: {
    field: "id",
    ...idColDef,
    valueGetter: ({ data }) => Number.parseInt(data?.id || "0", 10),
  },
  status: {
    field: "status",
    headerName: "Status",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <PillCell
        value={data.status === "ACTIVE" ? "Active" : "Revoked"}
        valueToVariantMap={{ Active: "success", Revoked: "error" }}
      />
    ),
  },
  environment: {
    field: "envHandle",
    headerName: "Environment",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <EnvironmentCell id={data.environmentId} hideIcon={true} />
    ),
  },
  createdBy: {
    field: "createdBy",
    headerName: "Created By",
    valueGetter: ({ data }) => data?.createdBy.name,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <DataTableCell
        label={data.createdBy.name}
        description={data.createdBy.email}
      />
    ),
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  requests: {
    field: "totalRequests",
    headerName: "Requests",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <Tooltip
        fluid
        text={
          <div className="grid grid-cols-2 gap-1">
            <div className="font-semibold">Successful</div>
            <div className="text-right">
              {data.currentUsage.successfulRequests.toLocaleString()}
            </div>
            <div className="font-semibold">Failed</div>
            <div className="text-right">
              {data.currentUsage.failedRequests.toLocaleString()}
            </div>
          </div>
        }
      >
        <span className="border-b border-dotted border-gray-400">
          {compactNumber(data.totalRequests)}
        </span>
      </Tooltip>
    ),
  },
  tokens: {
    field: "totalTokens",
    headerName: "Tokens",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <Tooltip
        fluid
        text={
          <div className="grid grid-cols-2 gap-1">
            <div className="font-semibold">Input</div>
            <div className="text-right">
              {data.currentUsage.inputTokens.toLocaleString()}
            </div>
            <div className="font-semibold">Output</div>
            <div className="text-right">
              {data.currentUsage.outputTokens.toLocaleString()}
            </div>
          </div>
        }
      >
        <span className="border-b border-dotted border-gray-400">
          {compactNumber(data.totalTokens)}
        </span>
      </Tooltip>
    ),
  },
  spend: {
    field: "spend",
    headerName: "Spend",
    sort: "desc",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <CurrencyCell value={data.spend} />
    ),
  },
};

const LLM_KEYS_TITLE_DESCRIPTION =
  "LLM keys provide secure access to the Aptible LLM Gateway for your applications.";

export const LlmKeyListByOrg = () => {
  const orgId = useSelector(selectOrganizationSelectedId);
  const [keyToRevoke, setKeyToRevoke] = useState<DeployLlmKeyRow | null>(null);

  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchLlmGatewayConfiguration,
    fetchLlmKeys,
  ]);

  const config = useSelector(selectLlmGatewayConfiguration);
  const isEnrolled = !!config?.id;
  const llmKeys = useSelector(selectLlmKeysForTable);
  const hasLlmKeys = llmKeys.length > 0;

  const llmKeyListByOrgColDefs = useMemo(
    () => [
      colDefs.handle,
      colDefs.id,
      colDefs.status,
      colDefs.environment,
      colDefs.createdBy,
      colDefs.createdAt,
      colDefs.requests,
      colDefs.tokens,
      colDefs.spend,
      {
        colId: "actions",
        headerName: "Actions",
        sortable: false,
        width: 100,
        resizable: false,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
          <Button
            variant="delete"
            size="sm"
            disabled={data.status === "REVOKED"}
            onClick={() => setKeyToRevoke(data)}
          >
            Revoke
          </Button>
        ),
      },
    ],
    [],
  );

  if (isLoading || isInitialLoading) {
    return (
      <Group>
        <Group size="sm">
          <TitleBar description={LLM_KEYS_TITLE_DESCRIPTION}>
            LLM Gateway
          </TitleBar>
        </Group>
        <div className="flex justify-center py-12">
          <LoadingSpinner show />
        </div>
      </Group>
    );
  }

  if (!isEnrolled) {
    return <Navigate to={aiGatewayEnrollUrl()} replace />;
  }

  return (
    <Group>
      <Group variant="horizontal" className="items-center justify-between">
        <TitleBar description={LLM_KEYS_TITLE_DESCRIPTION}>
          LLM Gateway
        </TitleBar>
      </Group>

      {/*
       * Note: we're not using an existing banner component here because this
       * is more of a temporary "promo" banner during the beta period, which
       * warrants custom styling.
       */}
      {!isLoading && !hasLlmKeys && (
        <CollapsibleBanner
          title="Welcome to the LLM Gateway!"
          defaultExpanded={true}
          feature="ai-gateway-welcome"
          dismissable={false}
          persistDismissed={false}
          icon={<div className="text-2xl">🎉</div>}
          extraActions={
            <ButtonLink variant="white" size="sm" to={supportUrl()}>
              Contact Support
            </ButtonLink>
          }
        >
          <div className="flex-1">
            <p className={`${tokens.type.darker} max-w-[95%]`}>
              You're on your way to using LLMs safely and securely in a
              compliant environment. Need help getting started?{" "}
              <ExternalLink
                className="underline"
                href="https://www.aptible.com/docs/core-concepts/ai-gateway"
              >
                Check out the docs
              </ExternalLink>
              , or contact us if you'd like some assistance getting setup.
            </p>
          </div>
        </CollapsibleBanner>
      )}

      {!isLoading && !hasLlmKeys && (
        <Banner variant="warning">
          <p className={`${tokens.type.darker} mt-0.5`}>
            A signed BAA with Aptible is required before you can safely send any
            PHI through the gateway. Don't have one?{" "}
            <Link to={supportUrl()} className="underline">
              Contact us
            </Link>
            .
          </p>
        </Banner>
      )}

      <OrgAiUsageSummary orgId={orgId} />

      <DataTable<DeployLlmKeyRow>
        rowData={llmKeys}
        colDefs={llmKeyListByOrgColDefs}
        syncQueryParams={true}
        defaultSearch="status:active"
      />

      <RevokeKeyModal
        isOpen={keyToRevoke !== null}
        onClose={() => setKeyToRevoke(null)}
        llmKey={keyToRevoke}
      />
    </Group>
  );
};

// --- Environment variant ---

export const LlmKeyListByEnvironment = ({ envId }: { envId: string }) => {
  useQuery(fetchEnvironmentById({ id: envId }));
  const orgId = useSelector(selectOrganizationSelectedId);
  const [keyToRevoke, setKeyToRevoke] = useState<DeployLlmKeyRow | null>(null);

  useQuery(fetchLlmGatewayConfiguration({ orgId }));
  useQuery(fetchLlmKeysByEnvId({ envId, orgId }));
  useQuery(fetchLlmPolicy({ envId, orgId }));
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchLlmGatewayConfiguration,
    fetchLlmKeysByEnvId,
    fetchLlmPolicy,
  ]);

  const config = useSelector(selectLlmGatewayConfiguration);
  const isEnrolled = !!config?.id;
  const llmKeys = useSelector((s) =>
    selectLlmKeysByEnvIdForTable(s, { envId }),
  );

  const llmKeyListByEnvColDefs = useMemo(
    () => [
      colDefs.handle,
      colDefs.id,
      colDefs.status,
      colDefs.createdBy,
      colDefs.createdAt,
      colDefs.requests,
      colDefs.tokens,
      colDefs.spend,
      {
        colId: "actions",
        headerName: "Actions",
        sortable: false,
        width: 100,
        resizable: false,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
          <Button
            variant="delete"
            size="sm"
            disabled={data.status === "REVOKED"}
            onClick={() => setKeyToRevoke(data)}
          >
            Revoke
          </Button>
        ),
      },
    ],
    [],
  );

  if (isLoading || isInitialLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner show />
      </div>
    );
  }

  if (!isEnrolled) {
    return <Navigate to={aiGatewayEnrollUrl()} replace />;
  }

  return (
    <Group size="lg">
      <LlmPolicyForEnvironment envId={envId} orgId={orgId ?? ""} />

      <DataTable<DeployLlmKeyRow>
        rowData={llmKeys}
        colDefs={llmKeyListByEnvColDefs}
        syncQueryParams={true}
        isLoading={!llmKeys.length && (isLoading || isInitialLoading)}
      />

      <RevokeKeyModal
        isOpen={keyToRevoke !== null}
        onClose={() => setKeyToRevoke(null)}
        llmKey={keyToRevoke}
      />
    </Group>
  );
};
